import { Phone, Star } from 'lucide-react';
import { VehicleIllustration } from '../../components/VehicleIllustration';
import { Avatar, Card } from '@tcrd/ui';
import type { RideDto } from '../../lib/types';

/** Conductor y vehículo tal como los ve el cliente (sin documentos privados). */
export function DriverVehicleCard({ ride }: { ride: RideDto }) {
  const v = ride.vehicle;
  const photo = v?.photos.find((p) => p.kind === 'MAIN') ?? v?.photos[0];
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Avatar src={ride.driver.photoUrl} firstName={ride.driver.firstName} lastName={ride.driver.lastName} size={56} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold" data-person>{ride.driver.firstName} {ride.driver.lastName}</p>
          {Number(ride.driver.rating) > 0 && (
            <p className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Star className="size-3.5 fill-amber-400 text-amber-400" aria-hidden /> {ride.driver.rating}</p>
          )}
        </div>
        {ride.driver.phone && (
          <a href={`tel:${ride.driver.phone}`} className="tap size-12 rounded-full bg-brand-black text-white dark:bg-white dark:text-brand-black flex items-center justify-center" aria-label="Llamar al conductor">
            <Phone className="size-5" />
          </a>
        )}
      </div>
      {v && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[var(--surface-2)] p-3">
          {photo ? <img src={photo.url} alt={`${v.make} ${v.model}`} className="h-14 w-20 rounded-xl object-cover" /> : <VehicleIllustration type={v.type} color={v.color} className="h-14 w-24" title={`${v.make} ${v.model} ${v.color}`} />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold">{v.make} {v.model} <span className="font-normal text-[var(--text-muted)]">· {v.color}</span></p>
            <p className="text-sm tracking-widest font-mono font-bold">{v.plate}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

/** El cliente tal como lo ve el conductor. */
export function CustomerCard({ ride }: { ride: RideDto }) {
  const c = ride.customer;
  if (!c) return null;
  const prefs = Object.entries(c.preferences ?? {}).filter(([, v]) => v);
  const PREF_LABEL: Record<string, Record<string, string>> = {
    temperature: { cool: 'A/C fresco', mild: 'A/C moderado', off: 'Sin A/C' },
    music: { yes: 'Con música', quiet: 'Sin música' },
    conversation: { chatty: 'Le gusta conversar', quiet: 'Prefiere silencio' },
  };
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Avatar src={c.photoUrl} firstName={c.firstName} lastName={c.lastName} size={56} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold">{c.contactName ?? `${c.firstName} ${c.lastName}`}</p>
          <p className="text-xs text-[var(--text-muted)]">{ride.passengers} pasajero(s) · {ride.luggage} maleta(s)</p>
        </div>
        {c.phone && (
          <a href={`tel:${c.phone}`} className="tap size-12 rounded-full bg-brand-black text-white dark:bg-white dark:text-brand-black flex items-center justify-center" aria-label="Llamar al cliente">
            <Phone className="size-5" />
          </a>
        )}
      </div>
      {prefs.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {prefs.map(([k, v]) => <li key={k} className="rounded-full bg-black/5 dark:bg-white/10 px-3 py-1 text-xs">{PREF_LABEL[k]?.[v] ?? v}</li>)}
        </ul>
      )}
    </Card>
  );
}
